/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {CachedData} from '../reducers/groups-filter';
import {GroupsTreeFilterState} from '../types';

const selectTreeNodes = ({groupsFilter: {treeNodes}}: GroupsTreeFilterState) => treeNodes;
const selectTreeNodeKeys = ({groupsFilter: {nodeKeyMap}}: GroupsTreeFilterState) => nodeKeyMap;
const selectAssets = ({groupsFilter: {assetKeyMap}}: GroupsTreeFilterState) => assetKeyMap;
const selectTrackingExpandedKeys =
  ({groupsFilter: {expandedNodesTracking}}: GroupsTreeFilterState) => expandedNodesTracking;
const selectTrackingCheckedKeys =
  ({groupsFilter: {checkedNodesTracking}}: GroupsTreeFilterState) => checkedNodesTracking;

const selectHistoryExpandedKeys =
  ({groupsFilter: {expandedNodesHistory}}: GroupsTreeFilterState) => expandedNodesHistory;
const selectHistoryCheckedKeys =
  ({groupsFilter: {checkedNodesHistory}}: GroupsTreeFilterState) => checkedNodesHistory;
const selectShipmentsExpandedKeys =
  ({groupsFilter: {expandedNodesShipments}}: GroupsTreeFilterState) => expandedNodesShipments;
const selectShipmentsCheckedKeys =
  ({groupsFilter: {checkedNodesShipments}}: GroupsTreeFilterState) => checkedNodesShipments;
const selectAssetLoading =
  ({groupsFilter: {loadingPending}}: GroupsTreeFilterState) => loadingPending;
const selectIncludeSCDevices =
  ({groupsFilter: {includeSCDevices}}: GroupsTreeFilterState) => includeSCDevices;
const selectSCDeviceTypes =
  ({groupsFilter: {SCDeviceTypes}}: GroupsTreeFilterState) => SCDeviceTypes;

export const treeNodesSelector = createDeepEqualSelector(selectTreeNodes,
  (treeNodes) => treeNodes
);

export const getTreeNodeKeysSelector = createDeepEqualSelector(selectTreeNodeKeys,
  (nodeKeyMap) => nodeKeyMap
);

export const treeKeysTrackingSelector = createDeepEqualSelector(
  selectTrackingExpandedKeys,
  selectTrackingCheckedKeys,
  (expandedKeys, checkedKeys) => ({expandedKeys, checkedKeys})
);

export const treeKeysHistorySelector = createDeepEqualSelector(
  selectHistoryExpandedKeys,
  selectHistoryCheckedKeys,
  (expandedKeys, checkedKeys) => ({expandedKeys, checkedKeys})
);

export const treeKeysShipmentsSelector = createDeepEqualSelector(
  selectShipmentsExpandedKeys,
  selectShipmentsCheckedKeys,
  (expandedKeys, checkedKeys) => ({expandedKeys, checkedKeys})
);

export const groupsFilterTrackingRootAccountSelector = createSelector(
  selectTreeNodeKeys,
  (nodeKeyMap) => nodeKeyMap?.['0']
);

export const groupsFilterHistoryRootAccountSelector = createSelector(
  selectTreeNodeKeys,
  (nodeKeyMap) => nodeKeyMap?.['0']
);

export const includeSCDeviceSelector = createSelector(
  selectIncludeSCDevices,
  (includeSCDevices): boolean => includeSCDevices
);

export const groupsFilterShipmentsRootAccountSelector = createSelector(
  selectTreeNodeKeys,
  (nodeKeyMap) => nodeKeyMap?.['0']
);

export const assetLoadingSelector = createSelector(selectAssetLoading,
  (loadingPending: boolean): boolean => loadingPending
);

export const assetsStoredSelector = createDeepEqualSelector(
  selectAssets,
  (assetKeyMap): CachedData => assetKeyMap
);

export const scDeviceTypesSelector = createSelector(
  selectSCDeviceTypes,
  (SCDeviceTypes): string => SCDeviceTypes
);
